/**
 * StreamCast Application Entry Point
 */

class App {
    constructor() {
        this.currentPage = 'home';
        this.pages = {};
        this.currentUser = null;

        // Initialize components
        this.player = new VideoPlayer();
        this.channelList = new ChannelList();
        this.sourceManager = new SourceManager();
        this.epgGuide = new EpgGuide();

        // Initialize page controllers - Home/Guide are needed immediately
        // (Home on load, Guide for its EPG preload below). Movies/Series/
        // Settings/Watch are constructed lazily on first visit via
        // ensurePage(), backed by window.__lazyPageImports (see entry.js).
        this.pages.home = new HomePage(this);
        this.pages.guide = new GuidePage(this);

        this.init();
    }

    /**
     * Lazily import (if needed) and construct a page controller on first
     * use. Safe to call repeatedly - returns the existing instance once
     * constructed. Movies/Series/Settings/Watch aren't in the DOM's script
     * list at all post-bundle; anything that reaches into
     * `this.pages.<name>` for one of those four must await this first
     * (navigateTo() already does; playMovie/playEpisode/playItem/
     * navigateToSeries call it directly since they reach WatchPage/
     * SeriesPage without necessarily having navigated there first).
     */
    async ensurePage(pageName) {
        if (this.pages[pageName]) return this.pages[pageName];

        const importFn = window.__lazyPageImports?.[pageName];
        if (importFn) await importFn();

        const ctorNames = {
            movies: 'MoviesPage',
            series: 'SeriesPage',
            settings: 'SettingsPage',
            watch: 'WatchPage',
        };
        const ctorName = ctorNames[pageName];
        if (ctorName && window[ctorName] && !this.pages[pageName]) {
            this.pages[pageName] = new window[ctorName](this);
        }

        return this.pages[pageName];
    }

    async init() {
        // Release any active transcode session's provider connection the
        // moment this tab/app actually closes, even when that happens by
        // just closing it rather than clicking Logout (which already
        // triggers its own cleanup - see the logout handler below). Without
        // this, an orphaned session would sit on the 90s idle reaper instead,
        // and this account's IPTV line only allows 1 concurrent connection -
        // so a login shortly after closing the tab could hit the same
        // "video source unavailable" (HTTP 551) the reaper exists to avoid.
        // fetch() gets cancelled by the browser once a page is actually
        // unloading, so this needs navigator.sendBeacon() specifically - it's
        // guaranteed to still be sent even after the page is gone, but only
        // supports POST (see server/routes/transcode.js's beacon-stop route).
        // pagehide (not the deprecated unload, and not visibilitychange -
        // switching tabs/apps to keep listening/watching in the background
        // must NOT stop playback) is the standard event for this; beforeunload
        // is added too as a second chance on browsers/situations where
        // pagehide doesn't fire reliably (e.g. older Safari quirks) - firing
        // both is harmless, a session already removed by the first beacon
        // just no-ops on the second.
        const sendStopBeacons = () => {
            const sessionIds = [this.player?.currentSessionId, this.pages?.watch?.currentSessionId]
                .filter(Boolean);
            for (const id of sessionIds) {
                navigator.sendBeacon(`/api/transcode/${id}/beacon-stop`);
            }
        };
        window.addEventListener('pagehide', sendStopBeacons);
        window.addEventListener('beforeunload', sendStopBeacons);

        // Check authentication first
        await this.checkAuth();

        // Mobile menu toggle
        const mobileMenuToggle = document.getElementById('mobile-menu-toggle');
        const navbarMenu = document.getElementById('navbar-menu');

        if (mobileMenuToggle && navbarMenu) {
            mobileMenuToggle.addEventListener('click', () => {
                mobileMenuToggle.classList.toggle('active');
                navbarMenu.classList.toggle('active');
            });

            // Close menu when a nav link is clicked
            document.querySelectorAll('.nav-link').forEach(link => {
                link.addEventListener('click', () => {
                    mobileMenuToggle.classList.remove('active');
                    navbarMenu.classList.remove('active');
                });
            });

            // Close menu when clicking outside
            document.addEventListener('click', (e) => {
                if (!e.target.closest('.navbar')) {
                    mobileMenuToggle.classList.remove('active');
                    navbarMenu.classList.remove('active');
                }
            });
        }

        // Navigation handling. Only elements that actually declare a page to
        // go to - the Logout link (added by addLogoutButton(), also class
        // "nav-link" so the mobile-menu-close loop above still catches it)
        // has no data-page and must be skipped here, otherwise it picks up
        // a SECOND click handler that fires alongside its own real one and
        // calls navigateTo(undefined) - pushState() then stringifies that
        // into the literal URL "#undefined" and blanks every page (nothing
        // has id="page-undefined"), racing against the real handler's
        // redirect to /login.html. Reproduced and confirmed via the
        // "https://tv.margero.com/#undefined" report.
        document.querySelectorAll('.nav-link').forEach(link => {
            if (!link.dataset.page) return;
            link.addEventListener('click', (e) => {
                e.preventDefault();
                this.navigateTo(link.dataset.page);
            });
        });

        // Now Playing indicator
        const nowPlayingBtn = document.getElementById('now-playing-indicator');
        if (nowPlayingBtn) {
            nowPlayingBtn.addEventListener('click', (e) => {
                e.preventDefault();
                this.navigateTo('watch');
            });
        }

        // Search clear buttons (global handler for all)
        document.querySelectorAll('.search-clear').forEach(btn => {
            btn.addEventListener('click', () => {
                const wrapper = btn.closest('.search-wrapper');
                const input = wrapper?.querySelector('.search-input');
                if (input) {
                    input.value = '';
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                    input.focus();
                }
            });
        });

        // Handle browser back/forward buttons
        window.addEventListener('popstate', (e) => {
            const page = e.state?.page || 'home';
            this.navigateTo(page, false); // false = don't add to history
        });

        // Initialize home page first (it's needed for channel list)
        await this.pages.home.init();

        // Preload EPG data in the background, but during IDLE time - not
        // immediately during boot. Even after dropping descriptions the EPG is
        // the app's single largest download/parse (~6MB gzipped / ~38MB JSON),
        // and nothing on the initial screen (Home doesn't use EPG) needs it -
        // only the TV Guide does, and it loads on open anyway if this hasn't
        // finished. Deferring keeps the EPG fetch+parse from competing with the
        // paint of whatever page the user actually lands on.
        //
        // Explicitly ensures the channel list is loaded first (awaiting it,
        // not just hoping Home's own dashboard load already finished first -
        // that's a real race at this point in boot) and passes it to
        // loadEpg() so this preload can scope its fetch to favorited
        // channels too, same as a direct Guide navigation - otherwise
        // whichever caller reaches loadEpg() first "wins" its de-dup guard
        // for everyone, and this preload firing early with no channel data
        // yet would silently lock in the slow unscoped full-catalog fetch
        // even for a user who then opens Guide moments later with channels
        // (and favorites) already resolved.
        const preloadEpg = async () => {
            try {
                if (!this.channelList.channels || this.channelList.channels.length === 0) {
                    await this.channelList.loadSources();
                    await this.channelList.loadChannels();
                }
                await this.epgGuide.loadEpg(this.channelList.channels);
            } catch (err) {
                console.warn('Background EPG load failed:', err.message);
            }
        };
        if ('requestIdleCallback' in window) {
            requestIdleCallback(preloadEpg, { timeout: 8000 });
        } else {
            setTimeout(preloadEpg, 3000);
        }

        // Navigate to the page from URL hash, or default to home. Checked
        // against the known page-name set rather than `this.pages[hash]` -
        // movies/series/settings/watch aren't constructed yet at this point
        // (lazy-loaded, see ensurePage()), so an existence check here would
        // always be false for them and silently strand a hash-reload (e.g.
        // a hard reload to #movies) back on home.
        const validPages = new Set(['home', 'guide', ...Object.keys(window.__lazyPageImports || {})]);
        const hash = window.location.hash.slice(1); // Remove #
        const initialPage = hash && validPages.has(hash) ? hash : 'home';
        this.navigateTo(initialPage, true); // true = replace history (don't add)

        console.log('StreamCast initialized');
    }

    async checkAuth() {
        const token = localStorage.getItem('authToken');

        if (!token) {
            // No token, redirect to login (replace to avoid back button issues)
            window.location.replace('/login.html');
            return;
        }

        try {
            // Verify token with server
            const response = await fetch('/api/auth/me', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Invalid token');
            }

            this.currentUser = await response.json();

            // Hide settings for viewers
            if (this.currentUser.role === 'viewer') {
                const settingsLink = document.querySelector('.nav-link[data-page="settings"]');
                if (settingsLink) {
                    settingsLink.style.display = 'none';
                }
            }

            // Add logout button to navbar
            this.addLogoutButton();

        } catch (err) {
            console.error('Authentication error:', err);
            localStorage.removeItem('authToken');
            window.location.replace('/login.html');
        }
    }

    addLogoutButton() {
        const navbar = document.querySelector('.navbar-menu');
        if (!navbar || document.getElementById('logout-btn')) return;

        const logoutLink = document.createElement('a');
        logoutLink.href = '#';
        logoutLink.className = 'nav-link';
        logoutLink.id = 'logout-btn';
        logoutLink.innerHTML = `
            <span class="nav-icon"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="icon">
                <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
            </svg></span>
            <span>Logout</span>
        `;

        logoutLink.addEventListener('click', async (e) => {
            e.preventDefault();

            // Stop any active playback first, so its transcode session's own
            // DELETE (see WatchPage.js/VideoPlayer.js stop()) has already
            // fired before logout's own server-side cleanup runs - purely
            // for a cleaner teardown (fewer stray failed requests during the
            // transition); logout itself now also kills every session
            // server-side regardless, since this account's IPTV line only
            // allows 1 connection and a still-running session would
            // otherwise block the next login from playing anything.
            this.player?.stop?.();
            this.pages?.watch?.stop?.();

            const token = localStorage.getItem('authToken');
            if (token) {
                await fetch('/api/auth/logout', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
            }

            localStorage.removeItem('authToken');
            window.location.replace('/login.html');
        });

        navbar.appendChild(logoutLink);
    }

    /**
     * Update the "Now Playing" label in the TV Guide's topbar. Called
     * whenever a channel/catch-up programme is selected (see EpgGuide.js's
     * playChannel()/playCatchupProgram()) - Live TV playback happens
     * entirely within the Guide page now, there's no separate Live TV page
     * to navigate to/from.
     */
    updateGuideOverlayNowPlaying(name) {
        const el = document.getElementById('guide-overlay-channel-name');
        if (el) el.textContent = name || 'No channel selected';
    }

    async navigateTo(pageName, replaceHistory = false) {
        // Guards against the class of bug fixed 2026-08-13 where a stray
        // caller (a nav-link with no data-page) could get here with
        // `undefined` and have it silently stringify into the URL itself
        // (pushState's `#${pageName}` -> literal "#undefined") while also
        // blanking every page - fail loudly instead of corrupting state.
        if (!pageName) {
            console.error('[App] navigateTo() called with no pageName - ignoring', new Error().stack);
            return;
        }

        // Don't navigate if already on this page
        if (this.currentPage === pageName && !replaceHistory) {
            return;
        }

        // Update browser history
        if (replaceHistory) {
            // Replace current history entry (used on initial load)
            history.replaceState({ page: pageName }, '', `#${pageName}`);
        } else {
            // Add new history entry
            history.pushState({ page: pageName }, '', `#${pageName}`);
        }

        // Update nav
        document.querySelectorAll('.nav-link').forEach(link => {
            link.classList.toggle('active', link.dataset.page === pageName);
        });

        // Update pages
        document.querySelectorAll('.page').forEach(page => {
            page.classList.toggle('active', page.id === `page-${pageName}`);
        });

        // Notify page controllers - only the page being LEFT, never the
        // destination (replaceHistory=true means "re-enter/replace history
        // for this same page", e.g. WatchPage.play() starting a new
        // episode while already on 'watch' - hide()ing that same page
        // would undo state the caller just set, e.g. cancelNextEpisode()
        // re-dismissing the up-next panel play() had just re-armed).
        if (this.currentPage !== pageName && this.pages[this.currentPage]?.hide) {
            this.pages[this.currentPage].hide();
        }

        this.currentPage = pageName;

        // Lazily import + construct the target page controller if this is
        // its first visit (no-op if it already exists).
        await this.ensurePage(pageName);

        if (this.pages[pageName]?.show) {
            this.pages[pageName].show();
        }
    }
}

// Start app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    window.app = new App();

    // Cache-first static assets (JS/CSS/icons) on repeat visits - see
    // sw.js for exactly what it does and doesn't cache (never the API,
    // catalog data, or live streams).
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.register('/sw.js').catch(err => {
            console.warn('[SW] Registration failed:', err);
        });
    }

    // "Install app" banner. beforeinstallprompt only fires when the browser
    // has actually determined this page is installable (valid manifest +
    // registered service worker + HTTPS - see manifest.json/sw.js above),
    // so showing the banner only in response to that event (rather than
    // unconditionally) means it never appears somewhere it can't do
    // anything - notably iOS Safari, which never fires this event at all
    // (those users already get a working "Add to Home Screen" via the
    // Share sheet, since the apple-touch-icon/apple-mobile-web-app-* meta
    // tags are in place - just without this custom prompt). Dismissal is
    // remembered indefinitely (a "no thanks" shouldn't come back to nag
    // again next session).
    let deferredInstallPrompt = null;
    const installBanner = document.getElementById('install-banner');
    const INSTALL_DISMISS_KEY = 'installBannerDismissed';

    window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        if (localStorage.getItem(INSTALL_DISMISS_KEY)) return;
        if (window.matchMedia('(display-mode: standalone)').matches) return;
        deferredInstallPrompt = e;
        installBanner?.classList.remove('hidden');
    });

    document.getElementById('install-banner-install')?.addEventListener('click', async () => {
        if (!deferredInstallPrompt) return;
        installBanner?.classList.add('hidden');
        deferredInstallPrompt.prompt();
        await deferredInstallPrompt.userChoice;
        deferredInstallPrompt = null;
    });

    document.getElementById('install-banner-dismiss')?.addEventListener('click', () => {
        installBanner?.classList.add('hidden');
        localStorage.setItem(INSTALL_DISMISS_KEY, '1');
    });

    // Once actually installed, hide the banner and never show it again.
    window.addEventListener('appinstalled', () => {
        installBanner?.classList.add('hidden');
        localStorage.setItem(INSTALL_DISMISS_KEY, '1');
    });
});
